import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AngleDown from 'react-icons/lib/fa/angle-down';
import AngleUp from 'react-icons/lib/fa/angle-up';
import iconCheckMark from 'assets/images/check-mark.svg';
import ReactTooltip from 'react-tooltip';
import styles from './RegionAccordion.scss';
import { getRegionsForDisplay } from 'utils/regionStorage';

const RegionAccordion = ({ cities, profile, onSave, onClose }) => {
  const { t } = useTranslation();
  const [expandedCountries, setExpandedCountries] = useState(new Set());
  const [selectedCities, setSelectedCities] = useState(new Set());
  const [countriesData, setCountriesData] = useState({});

  useEffect(() => {
    if (!cities || cities.length === 0) {
      setCountriesData({});
      return;
    }

    const grouped = cities.reduce((acc, city) => {
      const countryName = city.country && city.country.name;
      if (!countryName) return acc;
      if (!acc[countryName]) {
        acc[countryName] = {
          name: countryName,
          cities: [],
          totalCount: 0,
        };
      }
      acc[countryName].cities.push(city);
      acc[countryName].totalCount += city.count || 0;
      return acc;
    }, {});

    setCountriesData(grouped);

    const { user_regions: userRegions } = getRegionsForDisplay(cities);

    let selectedCityIds = new Set();

    if (userRegions && userRegions.length > 0) {
      userRegions.forEach((userRegion) => {
        const city = cities.find(
          (c) =>
            c.name === userRegion.city && c.country && c.country.name === userRegion.country_name
        );
        if (city) {
          selectedCityIds.add(String(city.id));
        }
      });
    } else if (profile && profile.cityId) {
      const currentCityId = String(profile.cityId);
      const currentCity = cities.find((city) => String(city.id) === currentCityId);
      if (currentCity) {
        selectedCityIds.add(currentCityId);
      }
    }

    setSelectedCities(selectedCityIds);

    const countriesWithSelectedCities = new Set();
    selectedCityIds.forEach((cityId) => {
      const city = cities.find((c) => String(c.id) === cityId);
      if (city && city.country && city.country.name) {
        countriesWithSelectedCities.add(city.country.name);
      }
    });

    setExpandedCountries(countriesWithSelectedCities);

    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 0);
  }, [cities]);

  useEffect(() => {
    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 100);
  }, []);

  const toggleCountry = (countryName) => {
    const newExpanded = new Set(expandedCountries);
    if (newExpanded.has(countryName)) {
      newExpanded.delete(countryName);
    } else {
      newExpanded.add(countryName);
    }
    setExpandedCountries(newExpanded);

    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 0);
  };

  const isCountrySelected = (countryName) =>
    cities
      ? Array.from(selectedCities).some((cityId) => {
          const city = cities.find((c) => String(c.id) === cityId);
          return city && city.country && city.country.name === countryName;
        })
      : false;

  const selectCountry = (countryName) => {
    const countryData = countriesData[countryName];
    const countryCities = (countryData && countryData.cities) || [];

    if (countryCities.length === 0) return;

    const cityIds = countryCities.map((city) => String(city.id));
    const newSelectedCities = new Set(selectedCities);

    const allCitiesSelected = cityIds.every((id) => newSelectedCities.has(id));

    if (allCitiesSelected) {
      if (countryCities.length > 1) {
        return;
      }

      const selectedCitiesInCountry = cityIds.filter((id) => newSelectedCities.has(id));

      if (selectedCitiesInCountry.length === 1) {
        return;
      }

      cityIds.forEach((id) => newSelectedCities.delete(id));
    } else {
      const otherCountryCities = cities
        .filter((city) => city.country && city.country.name !== countryName)
        .map((city) => String(city.id));

      otherCountryCities.forEach((id) => newSelectedCities.delete(id));

      cityIds.forEach((id) => newSelectedCities.add(id));
    }

    setSelectedCities(newSelectedCities);
    setExpandedCountries(new Set([countryName]));

    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 0);
  };

  const toggleCity = (cityId) => {
    if (selectedCities.has(cityId)) {
      const selectedCity = cities.find((city) => String(city.id) === cityId);
      if (!selectedCity || !selectedCity.country) return;

      const selectedCountryName = selectedCity.country.name;

      const selectedCitiesInCountry = Array.from(selectedCities).filter((id) => {
        const city = cities.find((c) => String(c.id) === id);
        return city && city.country && city.country.name === selectedCountryName;
      });

      if (selectedCitiesInCountry.length === 1) {
        return;
      }

      const newSelectedCities = new Set(selectedCities);
      newSelectedCities.delete(cityId);
      setSelectedCities(newSelectedCities);
    } else {
      const selectedCity = cities.find((city) => String(city.id) === cityId);
      if (!selectedCity || !selectedCity.country) return;

      const selectedCountryName = selectedCity.country.name;

      const selectedCitiesFromOtherCountries = Array.from(selectedCities).some((id) => {
        const city = cities.find((c) => String(c.id) === id);
        return city && city.country && city.country.name !== selectedCountryName;
      });

      if (selectedCitiesFromOtherCountries) {
        const newSelectedCities = new Set([cityId]);
        setSelectedCities(newSelectedCities);
      } else {
        const newSelectedCities = new Set(selectedCities);
        newSelectedCities.add(cityId);
        setSelectedCities(newSelectedCities);
      }
    }

    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 0);
  };

  const handleSave = () => {
    if (selectedCities.size === 0) {
      return;
    }

    if (cities) {
      const selectedCityIds = Array.from(selectedCities);
      const selectedCitiesData = selectedCityIds
        .map((cityId) => cities.find((city) => String(city.id) === cityId))
        .filter(Boolean);

      if (selectedCitiesData.length > 0 && onSave) {
        if (selectedCitiesData.length === 1) {
          onSave(selectedCitiesData[0]);
        } else {
          onSave(selectedCitiesData);
        }
      }
    }
  };

  const isCitySelected = (cityId) => selectedCities.has(String(cityId));

  const isCityDisabled = (cityId) => {
    if (selectedCities.has(String(cityId))) {
      return false;
    }

    if (selectedCities.size === 0) {
      return false;
    }

    const currentCity = cities.find((city) => String(city.id) === cityId);
    if (!currentCity || !currentCity.country) {
      return true;
    }

    const currentCountryName = currentCity.country.name;

    const hasCitiesFromOtherCountries = Array.from(selectedCities).some((id) => {
      const city = cities.find((c) => String(c.id) === id);
      return city && city.country && city.country.name !== currentCountryName;
    });

    return hasCitiesFromOtherCountries;
  };

  return (
    <div className={styles.PopUpOuter}>
      <div className={styles.PopUpInner}>
        <div className={styles.PopupContent}>
          <div className={styles.PopupHeader}>
            <div className={styles.PopupTitle}>{t('components.header.regionPopup.title')}</div>
            <button className={styles.CloseButton} onClick={onClose || (() => {})}>
              ×
            </button>
          </div>

          <div className={styles.PopupBody}>
            <div className={styles.CountryList}>
              {Object.keys(countriesData).length === 0 ? (
                <div>{t('components.header.region.loadingData')}</div>
              ) : (
                Object.values(countriesData).map((country) => (
                  <div key={country.name} className={styles.CountryItem}>
                    <div className={styles.CountryHeader}>
                      <div className={styles.CountryInfo}>
                        <div
                          className={`${styles.CountryCheckbox} ${
                            isCountrySelected(country.name) ? styles.Selected : ''
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            selectCountry(country.name);
                          }}
                        >
                          {isCountrySelected(country.name) && (
                            <img src={iconCheckMark} alt="selected" />
                          )}
                        </div>
                        <span className={styles.CountryName}>
                          {country.name} ({country.totalCount})
                        </span>
                      </div>
                      <div
                        className={styles.CountryArrow}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCountry(country.name);
                        }}
                      >
                        {expandedCountries.has(country.name) ? <AngleUp /> : <AngleDown />}
                      </div>
                    </div>

                    {expandedCountries.has(country.name) && (
                      <div className={styles.CitiesList}>
                        {country.cities.map((city) => {
                          const disabled = isCityDisabled(city.id);
                          return (
                            <div
                              key={city.id}
                              className={`${styles.CityItem} ${disabled ? styles.Disabled : ''}`}
                              onClick={() => !disabled && toggleCity(String(city.id))}
                            >
                              <div
                                className={`${styles.CityCheckbox} ${
                                  isCitySelected(city.id) ? styles.Selected : ''
                                } ${disabled ? styles.Disabled : ''}`}
                                {...(disabled && {
                                  'data-tip': t(
                                    'components.header.regionPopup.singleRegionTooltip'
                                  ),
                                  'data-for': 'city-tooltip',
                                  'data-place': 'right',
                                })}
                              >
                                {isCitySelected(city.id) && (
                                  <img src={iconCheckMark} alt="selected" />
                                )}
                              </div>
                              <span
                                className={`${styles.CityName} ${disabled ? styles.Disabled : ''}`}
                              >
                                {city.name} ({city.count || 0})
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className={styles.PopupFooter}>
            <button className={styles.SaveButton} onClick={handleSave}>
              {t('components.header.regionPopup.save')}
            </button>
          </div>
        </div>
      </div>
      <ReactTooltip
        id="city-tooltip"
        effect="solid"
        place="right"
        type="dark"
        delayShow={300}
        getContent={(dataTip) => (
          <div style={{ padding: '8px', maxWidth: '200px', wordWrap: 'break-word' }}>{dataTip}</div>
        )}
      />
    </div>
  );
};

export default RegionAccordion;



// WEBPACK FOOTER //
// ./src/components/Header/Region/RegionAccordion/RegionAccordion.js